import { css, keyframes, styled } from '@withjoy/joykit/theme';
import { Box } from '../Box';
import { DIALOG_RISE_MOTION as motion } from './Modal.motion';

const slideIn = keyframes`from { transform: translateY(var(--joy-dialog-enter-distance, ${motion.enter.distance}px)); } to { transform: translateY(0); }`;
const slideOut = keyframes`from { transform: translateY(0); } to { transform: translateY(var(--joy-dialog-exit-distance, ${motion.exit.distance}px)); }`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeOut = keyframes`from { opacity: 1; } to { opacity: 0; }`;
const backdropIn = keyframes`from { opacity: 0; } to { opacity: ${motion.backdrop.opacity}; }`;
const backdropOut = keyframes`from { opacity: ${motion.backdrop.opacity}; } to { opacity: 0; }`;

// Piecewise opacity keyframes preserve the measured curve on browsers without linear().
const backdropInSampled = keyframes`${motion.backdrop.easingStops.map(([percent, value]) => `${percent}% { opacity: ${value * motion.backdrop.opacity}; }`).join('\n')}`;
const backdropOutSampled = keyframes`${motion.backdrop.easingStops.map(([percent, value]) => `${percent}% { opacity: ${(1 - value) * motion.backdrop.opacity}; }`).join('\n')}`;

// Applied only to the explicit candidate preset. Existing Modal defaults remain unchanged.
export const dialogRiseStyles = css`
  &[data-dialog-motion='rise'] {
    &[data-state='open'] {
      animation:
        ${slideIn} calc(${motion.enter.duration}ms * var(--joy-dialog-motion-scale, 1)) ${motion.enter.easing} 0ms 1 normal both,
        ${fadeIn} calc(${motion.enter.fadeDuration}ms * var(--joy-dialog-motion-scale, 1)) linear 0ms 1 normal both;
    }
    &[data-state='closing'] {
      animation:
        ${slideOut} calc(${motion.exit.duration}ms * var(--joy-dialog-motion-scale, 1)) ${motion.exit.easing} 0ms 1 normal both,
        ${fadeOut} calc(${motion.exit.fadeDuration}ms * var(--joy-dialog-motion-scale, 1)) linear calc(${motion.exit.fadeDelay}ms * var(--joy-dialog-motion-scale, 1)) 1 normal both;
    }
  }
  &[data-dialog-motion] {
    &[data-state='closing'] {
      pointer-events: none;
    }
    &[data-skip-intro='true'][data-state='open'],
    &[data-motion-reduced='true'] {
      animation: none;
      transform: none;
      opacity: 1;
      &[data-state='closing'] {
        opacity: 0;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      animation: none;
      transform: none;
      opacity: 1;
      &[data-state='closing'] {
        opacity: 0;
      }
    }
  }
`;

export const MotionContent = styled(Box)`
  ${dialogRiseStyles}
`;

export const motionBackdropBaseStyles = { backgroundColor: motion.backdrop.color };

export const MotionBackdrop = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndices.modal};
  opacity: ${motion.backdrop.opacity};
  &[data-state='open'] {
    animation: ${backdropInSampled} calc(${motion.backdrop.enterDuration}ms * var(--joy-dialog-motion-scale, 1)) linear 0ms 1 normal both;
    @supports (animation-timing-function: ${motion.backdrop.easing}) {
      animation-name: ${backdropIn};
      animation-timing-function: ${motion.backdrop.easing};
    }
  }
  &[data-state='closing'] {
    pointer-events: none;
    animation: ${backdropOutSampled} calc(${motion.backdrop.exitDuration}ms * var(--joy-dialog-motion-scale, 1)) linear 0ms 1 normal both;
    @supports (animation-timing-function: ${motion.backdrop.easing}) {
      animation-name: ${backdropOut};
      animation-timing-function: ${motion.backdrop.easing};
    }
  }
  &[data-skip-intro='true'][data-state='open'],
  &[data-motion-reduced='true'] {
    animation: none;
    &[data-state='closing'] {
      opacity: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    &[data-state='closing'] {
      opacity: 0;
    }
  }
`;
